export const LAYOUT = {
  // Global layout
  topbarHeight: 80,

  // Responsive sidebar widths (scales up on larger screens)
  sidebarWidth: {
    base: 200, // 769-1440px
    large: 260, // 1441-1920px
    wide: 300, // 1921-2560px
    ultraWide: 340, // >2560px
  },

  // Responsive sidebar component sizes
  sidebarComponents: {
    buttonHeight: {
      base: 42,
      large: 48,
      wide: 54,
    },
    padding: {
      base: 20,
      large: 24,
      wide: 28,
    },
  },

  // Documents page specific
  documentsSidePadding: 60,

  // Publications shelf positioning (responsive shelf bottom values)
  shelfBottom: {
    mobile: -20, // < 480px
    tablet: -30, // 480-768px
    desktop: -40, // 768-1024px
    laptop: -50, // 1024-1600px
    wide: -70, // > 1600px
  },

  // Publications card dimensions
  publicationsBaseCard: {
    width: 162,
    height: 232,
    gap: 16,
  },

  // Publications column configuration
  publicationsColumns: {
    min: 2,
    max: 8,
    default: 6,
  },
} as const;

/**
 * Helper function to get sidebar width based on viewport width
 *
 * @param width - The viewport width in pixels
 * @returns The appropriate sidebar width
 */
export const getSidebarWidth = (width: number): number => {
  if (width <= 1440) return LAYOUT.sidebarWidth.base;
  if (width <= 1920) return LAYOUT.sidebarWidth.large;
  if (width <= 2560) return LAYOUT.sidebarWidth.wide;
  return LAYOUT.sidebarWidth.ultraWide;
};

/**
 * Helper function to get sidebar button height based on viewport width
 *
 * @param width - The viewport width in pixels
 * @returns The appropriate button height
 */
export const getSidebarButtonHeight = (width: number): number => {
  if (width <= 1920) return LAYOUT.sidebarComponents.buttonHeight.base;
  if (width <= 2560) return LAYOUT.sidebarComponents.buttonHeight.large;
  return LAYOUT.sidebarComponents.buttonHeight.wide;
};

/**
 * Helper function to get sidebar padding based on viewport width
 *
 * @param width - The viewport width in pixels
 * @returns The appropriate padding value
 */
export const getSidebarPadding = (width: number): number => {
  if (width <= 1920) return LAYOUT.sidebarComponents.padding.base;
  if (width <= 2560) return LAYOUT.sidebarComponents.padding.large;
  return LAYOUT.sidebarComponents.padding.wide;
};

/**
 * Helper function to get shelf bottom value based on width
 *
 * @param width - The available width in pixels
 * @returns The appropriate shelf bottom offset
 */
export const getShelfBottom = (width: number): number => {
  if (width < 480) return LAYOUT.shelfBottom.mobile;
  if (width < 768) return LAYOUT.shelfBottom.tablet;
  if (width < 1024) return LAYOUT.shelfBottom.desktop;
  if (width < 1600) return LAYOUT.shelfBottom.laptop;
  return LAYOUT.shelfBottom.wide;
};

// Export type
export type LayoutKey = keyof typeof LAYOUT;
